import { Action } from '@compass/client/types/case_record';
import type { UpdateSummaryRequest } from '@compass/client/types/summary';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { caseRecordService, summaryService } from '@/services/api';
import { queryKeys } from '@/services/query.config';
import { useStore } from '@/store/index';
import type { OrgProgramContextOverride } from '@/types/context';
import { isEligibilityCheckHumanAuditState } from '@/utils/caseStateHelpers';
import { isSummaryEditableWorkflow } from '@/utils/workflow';

// Hook to get all call summaries for a case
// eslint-disable-next-line complexity -- org/program overrides add a few extra guards to a single query hook.
export function useCallSummaries(
  caseId: string | null,
  enabled = true,
  options?: {
    refetchInterval?: number | false;
    contextOverride?: OrgProgramContextOverride;
  },
) {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const effectiveOrganizationId =
    options?.contextOverride?.organizationId || selectedOrganizationId;
  const effectiveProgramId = options?.contextOverride?.programId || selectedProgramId;

  const isEnabled = !!caseId && !!effectiveOrganizationId && !!effectiveProgramId;

  return useQuery({
    queryKey: ['summaries', 'calls', effectiveOrganizationId, effectiveProgramId, caseId],
    queryFn: () =>
      summaryService.getCallSummaries({
        organization_id: effectiveOrganizationId!,
        program_id: effectiveProgramId!,
        case_id: caseId!,
      }),
    enabled: isEnabled && enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    select: data => data?.summaries || [],
    refetchInterval: options?.refetchInterval,
  });
}

// Hook to get case summary
export function useCaseSummary(
  caseId: string | null,
  enabled = true,
  options?: { refetchInterval?: number | false },
) {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const isEnabled = !!caseId && !!selectedOrganizationId && !!selectedProgramId;

  return useQuery({
    queryKey: ['summaries', 'case', caseId],
    queryFn: async () => {
      return summaryService.getCaseSummary({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        case_id: caseId!,
      });
    },
    enabled: isEnabled && enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 errors
      if (error?.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// Hook to update a summary
export function useUpdateSummary(caseRecordId: string | null) {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation({
    mutationFn: (data: UpdateSummaryRequest) => {
      if (!caseRecordId || !selectedOrganizationId || !selectedProgramId) {
        throw new Error('Missing context for update summary');
      }
      return summaryService.updateSummary({
        organization_id: selectedOrganizationId,
        program_id: selectedProgramId,
        case_id: caseRecordId,
        summary_id: data.summary_id!,
        content: data.content,
        edited_by: data.edited_by,
        input_output_checksum: data.input_output_checksum,
      });
    },
    onSuccess: response => {
      // Update the cache directly with the response
      // The response contains the updated summary, wrap it to match GetSummaryResponse format
      const caseId = response.summary?.case_id;
      if (caseId) {
        // Cancel any in-flight refetches to prevent race conditions
        queryClient.cancelQueries({
          queryKey: ['summaries', 'case', caseId],
        });

        // Optimistically update summary in cache
        queryClient.setQueryData(['summaries', 'case', caseId], {
          summary: response.summary,
        });
      }

      toast.success('Summary updated successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update summary';
      toast.error(errorMessage);
    },
  });
}

// Hook to regenerate case summary via case action API
export function useRegenerateSummary(caseId: string, caseDetails?: any) {
  const queryClient = useQueryClient();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const [isRegenerating, setIsRegenerating] = useState(
    caseDetails?.status_flags?.is_summary_generation_in_progress === true,
  );

  // Sync local regenerating state with backend flag
  useEffect(() => {
    setIsRegenerating(caseDetails?.status_flags?.is_summary_generation_in_progress === true);
  }, [caseDetails?.status_flags?.is_summary_generation_in_progress]);

  const regenerateMutation = useMutation({
    mutationFn: () => {
      if (!selectedOrganizationId || !selectedProgramId) {
        throw new Error('Organization and program context required');
      }
      return caseRecordService.executeAction({
        organization_id: selectedOrganizationId,
        program_id: selectedProgramId,
        case_record_id: caseId,
        action: Action.ACTION_REGENERATE_CASE_SUMMARY,
      });
    },
    onMutate: () => {
      setIsRegenerating(true);
    },
    onError: (error: any) => {
      setIsRegenerating(false);
      const errorMessage = error?.response?.data?.message || 'Failed to regenerate summary';
      toast.error(errorMessage);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.cases.byId(caseId),
      });
      queryClient.invalidateQueries({
        queryKey: ['summaries', 'case', caseId],
      });
      toast.success('Summary regeneration started successfully');
    },
  });

  /** Trigger regeneration if conditions are met (BV human audit + editable workflow) */
  const regenerateSummary = useCallback(
    (isHumanAudit: boolean, workflowType?: string) => {
      if (
        isHumanAudit &&
        !isEligibilityCheckHumanAuditState(caseDetails?.state) &&
        isSummaryEditableWorkflow(workflowType)
      ) {
        regenerateMutation.mutateAsync().catch(() => {
          // Error is already handled by the mutation's onError handler
        });
      }
    },
    [caseDetails?.state, regenerateMutation],
  );

  return { isRegenerating, regenerateSummary };
}

// Hook to trigger the standalone disposition-calculation workflow (hermes AD-2089).
// Intended to be fired after a successful output save on human_audit for
// pbm / medbv / unifiedmedbv / transportation — matches the main-branch flow where
// the summary signal used to be fired from the UI on every output save.
export function useCalculateDisposition(caseId: string, caseDetails?: any) {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  const calculateMutation = useMutation({
    mutationFn: () => {
      if (!selectedOrganizationId || !selectedProgramId) {
        throw new Error('Organization and program context required');
      }
      return caseRecordService.executeAction({
        organization_id: selectedOrganizationId,
        program_id: selectedProgramId,
        case_record_id: caseId,
        action: Action.ACTION_CALCULATE_DISPOSITION,
      });
    },
    onError: () => {
      // Non-fatal: disposition recomputation failure shouldn't interrupt the user's save
      // (the output/disposition writes already succeeded). Swallow silently — the user
      // can still click Regenerate Summary to retry end-to-end.
    },
  });

  /** Fire the calculate-disposition action if conditions are met. */
  const calculateDisposition = useCallback(
    (isHumanAudit: boolean, workflowType?: string) => {
      if (
        isHumanAudit &&
        !isEligibilityCheckHumanAuditState(caseDetails?.state) &&
        isSummaryEditableWorkflow(workflowType)
      ) {
        calculateMutation.mutateAsync().catch(() => {
          // Handled by onError above.
        });
      }
    },
    [caseDetails?.state, calculateMutation],
  );

  return { calculateDisposition };
}
